/* OdmOsPlatformDetailsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./OdmOsPlatformDetailsViewRenderer");

class OdmOsPlatformDetailsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        MUF,
        solutionsManager,
        storageFactory,
        OdmOsPlatformEntity,
        OdmOsLinkEntity,
        OdmInstructions,
        ValueMap
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;
        this._userSession = userSession;
        this._storageFactory = storageFactory;
        this._solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = this._solutionDefinition.name || "";
        this._odmOsPlatform = undefined;
        this._onPlatformFeedUpdated = this._onPlatformFeedUpdated.bind(this);
        this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this);
        this._onPendingPatchesChanged = this._onPendingPatchesChanged.bind(this);
        this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this);
        this._onInstanceRemovedFromFeed = this._onInstanceRemovedFromFeed.bind(this);
        this._renderer = new Renderer(this);
        this._OdmOsPlatformEntity = OdmOsPlatformEntity;
        this._OdmOsLinkEntity = OdmOsLinkEntity;
        this._OdmInstructionsEntity = OdmInstructions;
        this._isEditingInstruction = false;
        this._ValueMap = ValueMap;
        this._manufacturerOptions = [];
        this._modelOptions = [];
        this._deviceTypeOptions = [];
        this.childViewOpen = false;
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerPlatformListeners() {
        this._odmOsPlatform.on("DirtyStateChanged", this._onDirtyStateChanged);
        this._odmOsPlatform.on("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._odmOsPlatform.on("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    _registerPlatformFeedListeners() {
        this._platformFeed.on("NewpageEventReceived", this._onPlatformFeedUpdated);
        this._platformFeed.on("AddEventReceived", this._onPlatformFeedUpdated);
        this._platformFeed.on("UpdateEventReceived", this._onPlatformFeedUpdated);
        this._platformFeed.on("InstanceRemoved", this._onInstanceRemovedFromFeed);
    }

    _unregisterPlatformListeners() {
        this._odmOsPlatform.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._odmOsPlatform.removeListener("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._odmOsPlatform.removeListener(
            "PristinePropertiesChanged",
            this._onPristinePropertiesChanged
        );
    }

    _unregisterPlatformFeedListeners() {
        this._platformFeed.removeListener("NewpageEventReceived", this._onPlatformFeedUpdated);
        this._platformFeed.removeListener("AddEventReceived", this._onPlatformFeedUpdated);
        this._platformFeed.removeListener("UpdateEventReceived", this._onPlatformFeedUpdated);
        this._platformFeed.removeListener("InstanceRemoved", this._onInstanceRemovedFromFeed);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get isEditingInstruction() {
        return this.getIsEditingInstruction();
    }

    getIsEditingInstruction() {
        return this._isEditingInstruction;
    }

    async shutdown() {
        //jshint ignore:line
        // Unregister platform listeners if platform exists
        if (!_.isUndefined(this._odmOsPlatform)) {
            this._unregisterPlatformListeners();
        }

        // Shutdown platform feed if it exists
        if (!_.isEmpty(this._platformFeed)) {
            this._unregisterPlatformFeedListeners();
            await this._platformFeed.shutdown();
        }

        this._unregisterListeners();

        return this._renderer.shutdown().then(
            () => {
                // Clean up entity references
                this._odmOsPlatform = undefined;
                this._OdmOsPlatformEntity = undefined;
                this._OdmOsLinkEntity = undefined;
                this._OdmInstructionsEntity = undefined;

                // Clean up feeds
                this._platformFeed = undefined;

                // Clean up editor and UI references
                this.childViewOpen = undefined;
                this.childViewName = undefined;
                this._gameEditor = undefined;
                this._modalUserFeedback = undefined;
                this._renderer = undefined;
                this._isEditingInstruction = undefined;

                // Clean up storage and session
                this._storageFactory = undefined;
                this._userSession = undefined;

                // Clean up solution references
                this._solutionDefinition = undefined;
                this._solutionName = undefined;

                // Clean up ValueMap and field options
                this._ValueMap = undefined;
                this._manufacturerOptions = undefined;
                this._modelOptions = undefined;
                this._deviceTypeOptions = undefined;

                // Clean up logger last
                this._logger = undefined;
            },
            (err) => {
                if (this._logger) {
                    this._logger.warn(
                        {error: err},
                        "OdmOsPlatformDetailsViewComponent.shutdown() error"
                    );
                }
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        if (!_.isNull(this._odmOsPlatform._pendingPatches)) {
            dirtyState = true;
            this._gameEditor.getTabCmpnt().setDirty(dirtyState);
        }

        this.emit("Dirty", dirtyState);

        let onBeforeUnloadFunc = () => {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    return this.savePlatform().then(() => undefined); // it will not close the tab if it will have something in result
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return Promise.resolve(this._odmOsPlatform.clearPendingPatches(true))
                            .then(() => this._modalUserFeedback.hideDisplayed())
                            .then(() => {
                                this._gameEditor.getTabCmpnt().setDirty(false);
                            })
                            .then(() => undefined);
                    } else {
                        console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        };
        if (this.isDirty()) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._gameEditor.clearOnBeforeUnload();
        }
    }

    _onPendingPatchesChanged(/*patches, oldPatches*/) {
        return;
    }

    _onPristinePropertiesChanged(pristineProperties, before) {
        this.emit("PlatformPropertiesChanged", pristineProperties, before);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    isDirty() {
        return this._odmOsPlatform.isDirty();
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    whenModelIsReady() {
        return this._gameEditor
            .whenPlatformIsReady()
            .then(() => {
                this._odmOsPlatform = this._gameEditor.getPlatform();
                this._registerPlatformListeners();
                return this._OdmOsPlatformEntity
                    .startOdmOsPlatformsFeed(
                        this._userSession.activeOrganization.id,
                        {},
                        {
                            id: true,
                            name: true,
                        },
                        {
                            name: 1,
                        }
                    )
                    .whenInitialized();
            })
            .then((platformFeed) => {
                this._platformFeed = platformFeed;
                this._registerPlatformFeedListeners();
                return this._loadFieldOptions();
            })
            .catch((err) => {
                this._logger.warn(
                    err,
                    "OdmOsPlatformDetailsViewComponent.whenModelIsReady() error"
                );
                this._modalUserFeedback.displayError({
                    errorMsg: "This ODM OS Platform cannot be opened.",
                    errorCode: "",
                });
                return Promise.reject(err);
            });
    }

    _onPlatformFeedUpdated(/*entity, patches, props, feed*/) {
        this.emit("PlatformFeedUpdated");
    }

    _onInstanceRemovedFromFeed(instance, cause) {
        if (cause === "Deleted") {
            return this._onPlatformFeedUpdated(instance);
        }
    }

    resetPlatformProperties() {
        this._odmOsPlatform.clearPendingPatches();
        this._odmOsPlatform.clearPendingRelationships();
        return Promise.resolve();
    }

    async isPlatformFormValid(rollbackInvalidData = false) {
        //jshint ignore:line
        const platform = this._odmOsPlatform;
        const newData = this._odmOsPlatform.getProperties();
        const oldData = this._odmOsPlatform.getPristineProperties();

        const requiredFieldsConfig = [
            {key: "name", label: "Name", setter: "setName"},
            {key: "manufacturer", label: "Manufacturer", setter: "setManufacturer"},
            {key: "model", label: "Model", setter: "setModel"},
            {key: "deviceType", label: "IoT Device Type", setter: "setDeviceType"},
        ];

        // Required Fields Validation
        const requiredFields = [];
        requiredFieldsConfig.forEach(({key, label, setter}) => {
            if (!newData[key]?.trim()) {
                requiredFields.push(label);
                if (rollbackInvalidData) {
                    platform[setter](oldData[key]?.trim() || "");
                }
            }
        });

        // Conditional OS Fields Validation
        const hasOsNameAndVersion = newData.osName?.trim() && newData.osVersion?.trim();
        const hasOsReleaseName = !!newData.osReleaseName?.trim();
        const osFieldsInvalid = !hasOsNameAndVersion && !hasOsReleaseName;

        // Error Message Construction
        if (requiredFields.length > 0 || osFieldsInvalid) {
            const parts = [];

            if (requiredFields.length > 0) {
                const verb = requiredFields.length === 1 ? "is" : "are";
                parts.push(`Make sure ${requiredFields.join(", ")} ${verb} filled`);
            }

            if (osFieldsInvalid) {
                parts.push("provide OS Name and OS Version together, or just the OS Release Name");
            }

            throw {
                errorMsg: `Please complete all required fields. ${parts.join(". In addition, ")}.`,
                errorCode: "FormIncomplete",
                title: "Error – Form Incomplete",
            };
        }
    }

    savePlatform() {
        return Promise.resolve()
            .then(() => {
                //do nothing unless this guy is dirty
                if (this._odmOsPlatform.isDirty()) {
                    return this.isPlatformFormValid().then(() => {
                        return this._odmOsPlatform.update();
                    });
                }
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "OdmOsPlatformDetailsViewComponent.savePlatform() error."
                );
                if (err.userRejected) {
                    return Promise.reject();
                }
                return this.displayError(err);
            });
    }

    _loadFieldOptions() {
        return this._ValueMap
            .loadValueMapByName(this._userSession.activeOrganization.id, "xdome")
            .then((vendorOsMapEntity) => {
                const fieldMappings = vendorOsMapEntity.valueMap;
                this._deviceTypeOptions = this._generateOptionsForField(fieldMappings.deviceType);
                this._manufacturerOptions = this._generateOptionsForField(
                    fieldMappings.manufacturer
                );
                this._modelOptions = this._generateOptionsForField(fieldMappings.model);
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "OdmOsPlatformDetailsViewComponent._loadFieldOptions() error - using empty options"
                );
                this._deviceTypeOptions = [];
                this._manufacturerOptions = [];
                this._modelOptions = [];
                return Promise.resolve();
            });
    }

    _generateOptionsForField(fieldMapObj) {
        if (!fieldMapObj) {
            return [];
        }
        return Object.keys(fieldMapObj)
            .filter((key) => fieldMapObj[key])
            .map((key) => ({
                [key]: key,
            }));
    }

    displayError(err) {
        if (err && err.errorCode === "NoPendingUpdates") {
            this.emit("PlatformPropertiesChanged");
            return Promise.reject();
        } else {
            if (err && err.errorCode === "EntityAlreadyExists") {
                err.errorMsg =
                    "A platform with these details already exists. Change the details or open the existing platform.";
                err.errorCode = "DuplicatePlatform";
                err.title = "Error - Duplicate Platform Detected";
            }
            return this._modalUserFeedback.hideDisplayed().then(() => {
                if (!err.userRejected) {
                    this._modalUserFeedback.displayError(err);
                }
            });
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = OdmOsPlatformDetailsViewComponent;

OdmOsPlatformDetailsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.solutionsManager",
    "$teve.storageFactory",
    "$teve.entities.OdmOsPlatform",
    "$teve.entities.OdmOsLink",
    "$teve.entities.OdmInstructions",
    "$teve.entities.ValueMap",
];

OdmOsPlatformDetailsViewComponent.$displayDetails = {
    type: "View",
    name: "IoT Platform Details",
    icon: "osmanager.odm_os_platform_edit",
    shown: true,
    title: "Details",
    order: 1,
};
